import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useUserAuthStore = defineStore('userAuth', () => {
  const ussid = ref(null)
  const token = ref(null)
  const auth = ref(false)
  const user = ref(null)
  const cashback = ref(null)
  const cashbackSettings = ref(null)
  const isTerminal = ref(false)
  const favoritesCount = ref(null)

  const getCashback = computed(() => {
    return cashback.value
  })
  const getCashbackSettings = computed(() => {
    return cashbackSettings.value
  })
  const getUserBalanceBK = computed(() => {
    return user.value?.bkBalance ?? null
  })
  const getUserBalance = computed(() => {
    return user.value?.balance ?? null
  })
  const getUserBonusBalance = computed(() => {
    return user.value?.bonusBalance ?? null
  })
  const getBirthday = computed(() => {
    return user.value?.birthday ?? null
  })
  const getGender = computed(() => {
    return user.value?.gender ?? null
  })
  const getUserBonusBalanceOn = computed(() => {
    return user.value?.bonusBalanceOn ?? null
  })
  const getUserCurrency = computed(() => {
    return user.value?.currency ?? null
  })
  const getUserRegion = computed(() => {
    return user.value?.region ?? null
  })
  const getUserCurrencyCode = computed(() => {
    return user.value?.currency.code ?? null
  })
  const getToken = computed(() => {
    return token.value
  })
  const getUserVerificationStatus = computed(() => {
    return user.value?.docsVerificationId ?? null
  })
  const getUser = computed(() => {
    return user.value
  })
  const getUserLanguage = computed(() => {
    return user.value?.language ? user.value?.language.toLowerCase() : null
  })
  const firstAuth = computed(() => {
    return !user.value?.lastLogin
  })
  const profileIsFilled = computed(() => {
    if (!user.value) {
      return false
    }
    return user.value.profileIsFilled
  })
  const userNeedVerification = computed(() => {
    if (!user.value) {
      return false
    }
    return user.value.needVerification
  })

  const userAccessToWithdraw = computed(() => {
    if (!user.value) {
      return {
        access: false,
        message: '',
        errors: [],
      }
    }
    return {
      access: user.value.withdrawAccess,
      message: user.value.withdrawAccessStr,
      errors: user.value.withdrawAccessErrors,
    }
  })
  const phoneApproved = computed(() => {
    if (!user.value) {
      return false
    }
    return user.value.phoneApproved
  })
  const emailApproved = computed(() => {
    if (!user.value) {
      return false
    }
    return user.value.emailApproved
  })
  const getAuth = computed(() => {
    return !!user.value
  })

  const profileIsApproved = computed(() => {
    return process.env.PLATFORM === 'olymp' ? user.value?.profileIsApproved : user.value?.bkProfileIsApproved
  })
  const profileIsApprovedSum = computed(() => {
    return process.env.PLATFORM === 'olymp' ? user.value?.profileIsApprovedSum : user.value?.bkProfileIsApproved
  })
  const personalDataIsFilled = computed(() => {
    return user.value?.personalDataIsFilled
  })
  const getFavoritesCount = computed(() => {
    return favoritesCount.value
  })
  const getIdentLevel = computed(() => {
    const requiredSteps = []
    if (!user.value.phoneApproved) {
      requiredSteps.push(1)
    }
    if (!user.value.emailApproved) {
      requiredSteps.push(2)
    }
    if ((!user.value?.birthday || user.value?.gender === null) && process.env.PLATFORM === 'olymp') {
      requiredSteps.push(3)
    }
    if (!user.value.documentNum || !user.value.securityQuestionAnswer || !user.value.personalDataIsFilled) {
      requiredSteps.push(4)
    }
    return requiredSteps
  })

  function updateUSSID(payload) {
    ussid.value = payload
  }

  function updateCashback(payload) {
    cashback.value = payload
  }

  function updateCashbackSettings(payload) {
    cashbackSettings.value = payload?.fastCashback
  }

  function updateUser(payload) {
    user.value = { ...user.value, ...payload }
  }

  function updateUserField({ key, value }) {
    if (key && key in user.value) {
      user.value[key] = value
    }
  }

  function updateUserFields(payload) {
    user.value = { ...user.value, ...payload }
  }

  function removeToken() {
    auth.value = false
    token.value = null
    user.value = null
    cashback.value = null
    ussid.value = null
  }

  function updateToken(payload) {
    token.value = payload
  }

  function updateUserWithdrawalsRequests(payload) {
    updateUserField({ key: 'countActiveWithdraw', value: payload })
  }

  function updateAuth(payload) {
    auth.value = payload
  }

  function updateFavoritesCount(payload) {
    favoritesCount.value = payload
  }

  return {
    // refs
    ussid,
    token,
    auth,
    user,
    cashback,
    cashbackSettings,
    isTerminal,
    favoritesCount,
    // computed
    getCashback,
    getCashbackSettings,
    getUserBalanceBK,
    getUserBalance,
    getUserBonusBalance,
    getUserBonusBalanceOn,
    getBirthday,
    getGender,
    getUserCurrency,
    getUserCurrencyCode,
    getUserRegion,
    getToken,
    getUserVerificationStatus,
    getUser,
    getUserLanguage,
    firstAuth,
    profileIsFilled,
    userNeedVerification,
    userAccessToWithdraw,
    phoneApproved,
    emailApproved,
    getAuth,
    profileIsApproved,
    profileIsApprovedSum,
    personalDataIsFilled,
    getFavoritesCount,
    getIdentLevel,
    //functions
    updateUSSID,
    updateCashback,
    updateCashbackSettings,
    updateUser,
    updateUserField,
    updateUserFields,
    removeToken,
    updateToken,
    updateAuth,
    updateFavoritesCount,
    updateUserWithdrawalsRequests,
  }
})
